import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { checkoutAdminAPI, employeeAPI, debugAPI, leaveAdminAPI, validationAPI, adminGroupsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import * as XLSX from 'xlsx';
import { isItalianHoliday } from '../utils/holidays';
import { useLocation, useNavigate } from 'react-router-dom';

const isRecordDateHoliday = (record: { date: string }) => {
  const [y, m, d] = record.date.split('-').map(Number);
  return isItalianHoliday(new Date(y, m - 1, d));
};
// @ts-ignore - xlsx-js-style non ha tipi TypeScript ufficiali
import * as XLSXStyle from 'xlsx-js-style';

interface LeaveSummary {
  type: string;
  hours: number | null;
  leave_reason?: string | null; // Causale permesso
  leave_request_id?: number; // ID per eliminazione
}

interface PresenceModificationRow {
  id: number;
  adminName: string;
  createdAt: string;
  oldCheckIn?: string | null;
  newCheckIn?: string | null;
  oldCheckOut?: string | null;
  newCheckOut?: string | null;
  oldWorkHours?: number | null;
  newWorkHours?: number | null;
  adminNote?: string | null;
}

interface DayRecord {
  presenceId?: number;
  date: string;
  dayOfWeek: string;
  check_in1: string | null;
  check_out1: string | null;
  check_in2: string | null;
  check_out2: string | null;
  total_hours: number | null;
  status: 'complete' | 'incomplete' | 'holiday' | 'absence' | 'leave';
  notes: string | null;
  leaves?: LeaveSummary[];
  expectedHours?: number | null;
  lastAdminUpdateAt?: string;
  lastAdminFullName?: string;
  lastAdminNote?: string | null;
  wasModified?: boolean;
  checkInRaw?: string | null;
  checkOutRaw?: string | null;
  modifications?: PresenceModificationRow[];
}

interface EmployeeOption {
  employeeId: number;
  userId: number;
  fullName: string;
  matricola?: string | null;
  dipartimento?: string | null;
  sede?: string | null;
  weeklyHours?: number | null;
  weeklyDays?: number | null;
  pausaPranzo?: number | null; // minuti
  groupId?: number | null;
  groupName?: string | null;
}

interface ModDialogData {
  date: string;
  modifications: PresenceModificationRow[];
}

const monthNames = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
];

const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

const getLeaveDisplay = (type: string) => {
  const normalized = type?.toLowerCase();
  switch (normalized) {
    case 'ferie':
      return { letter: 'F', color: 'primary' as const };
    case 'permesso':
      return { letter: 'P', color: 'warning' as const };
    case 'malattia':
      return { letter: 'M', color: 'error' as const };
    default:
      return { letter: normalized ? normalized.charAt(0).toUpperCase() : '-', color: 'default' as const };
  }
};

const AdminTimesheet: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isSuperAdmin, isAdmin, isGroupAdmin, loadingGroupCheck } = usePermissions();
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState<DayRecord[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingTimesheet, setLoadingTimesheet] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalHours, setTotalHours] = useState(0);
  const [workingDays, setWorkingDays] = useState(0);
  const [expectedHours, setExpectedHours] = useState<number | null>(null);
  const [expectedHoursPerDay, setExpectedHoursPerDay] = useState<Record<string, number>>({});
  const [editingPresenceId, setEditingPresenceId] = useState<number | null>(null);
  const [editedCheckIn, setEditedCheckIn] = useState('');
  const [editedCheckOut, setEditedCheckOut] = useState('');
  const [editedAdminNote, setEditedAdminNote] = useState('');
  const [savingPresence, setSavingPresence] = useState(false);
  const [editingExpectedDate, setEditingExpectedDate] = useState<string | null>(null);
  const [editedExpectedHours, setEditedExpectedHours] = useState('');
  const [savingExpected, setSavingExpected] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DayRecord | null>(null);
  const [modDialogOpen, setModDialogOpen] = useState(false);
  const [modDialogData, setModDialogData] = useState<ModDialogData | null>(null);
  const [pendingFlags, setPendingFlags] = useState<Record<number, boolean>>({});
  const [validatedFlags, setValidatedFlags] = useState<Record<number, boolean>>({});
  
  // Stati per validazione
  const [validationPreview, setValidationPreview] = useState<any>(null);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationNotes, setValidationNotes] = useState('');

  // Stati per gestione permessi
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveDialogDate, setLeaveDialogDate] = useState<string | null>(null);
  const [leaveDialogType, setLeaveDialogType] = useState<'permesso' | 'ferie' | 'malattia' | 'altri'>('permesso');
  const [leaveDialogHours, setLeaveDialogHours] = useState<number>(0);
  const [leaveDialogExpectedHours, setLeaveDialogExpectedHours] = useState<number>(8); // Ore giornaliere per max permesso
  const [leaveDialogReason, setLeaveDialogReason] = useState<string>('');
  const [leaveDialogNotes, setLeaveDialogNotes] = useState<string>('');
  const [savingLeave, setSavingLeave] = useState(false);
  const [deletingLeave, setDeletingLeave] = useState<number | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const refreshPendingFlagsForEmployees = useCallback(
    async (employeesList: EmployeeOption[], selectedYear: number, selectedMonth: number) => {
      if (!Array.isArray(employeesList) || employeesList.length === 0) {
        return {};
      }

      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const results = await Promise.all(
        employeesList.map(async (emp) => {
          try {
            const data = await checkoutAdminAPI.getUserHistory(emp.userId, startDate, endDate);
            const history = Array.isArray(data.history) ? data.history : [];
            const leaves = Array.isArray(data.leaves) ? data.leaves : [];
            const expectedDaily = buildExpectedDailyMap(data.expected_hours_per_day);
            const hasPending = computePendingFromHistory(history, leaves, expectedDaily, selectedYear, selectedMonth);
            return { userId: emp.userId, hasPending };
          } catch (error) {
            return { userId: emp.userId, hasPending: false };
          }
        })
      );

      const map: Record<number, boolean> = {};
      results.forEach(({ userId, hasPending }) => {
        map[userId] = hasPending;
      });

      return map;
    },
    []
  );

  // Carica le validazioni per i dipendenti
  const loadValidatedFlags = useCallback(
    async (employeesList: EmployeeOption[], selectedYear: number, selectedMonth: number) => {
      if (!Array.isArray(employeesList) || employeesList.length === 0) {
        return {};
      }

      try {
        // Carica tutte le validazioni per il mese/anno selezionato
        const response = await validationAPI.getValidationsForAdmin({
          year: selectedYear,
          month: selectedMonth,
        });

        const validations = response.validations || [];
        
        // Crea una mappa userId -> validato
        const validatedMap: Record<number, boolean> = {};
        
        // Per ogni validazione, trova il userId corrispondente dall'employee_id
        validations.forEach((validation: any) => {
          // Trova il dipendente corrispondente
          const employee = employeesList.find(emp => emp.employeeId === validation.employee_id);
          if (employee) {
            validatedMap[employee.userId] = true;
          }
        });
        return validatedMap;
      } catch (error) {
        return {};
      }
    },
    []
  );

  const monthOptions = useMemo(() => monthNames.map((label, index) => ({ value: index + 1, label })), []);
  const yearOptions = useMemo(
    () => Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i),
    []
  );
  const pendingCount = useMemo(
    () => Object.values(pendingFlags).filter(Boolean).length,
    [pendingFlags]
  );

  const toTimeFieldValue = (iso?: string | null) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const toDateAtTime = (dateStr: string, timeValue: string) => {
    const [hourStr, minuteStr] = timeValue.split(':');
    if (hourStr == null || minuteStr == null) return null;
    const hours = Number(hourStr);
    const minutes = Number(minuteStr);
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const formatLocalDateTimeForApi = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}:00`;
  };

  const getLocalDateKey = (value: Date = new Date()) => {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  };

  const combineDateAndTime = (dateStr: string, timeValue: string) => {
    if (!timeValue) return undefined;
    const date = toDateAtTime(dateStr, timeValue);
    if (!date) return undefined;
    // Preserve the local wall-clock time for Postgres timestamp fields.
    return formatLocalDateTimeForApi(date);
  };

  const calculateDurationHours = (
    dateStr: string,
    start: string | null,
    end: string | null,
    lunchBreakMinutes: number = 0
  ): number | null => {
    if (!start || !end) return null;
    const startDate = toDateAtTime(dateStr, start);
    const endDate = toDateAtTime(dateStr, end);
    if (!startDate || !endDate) return null;
    const diffMs = endDate.getTime() - startDate.getTime();
    const totalHours = diffMs / 3_600_000;
    // Sottrae la pausa pranzo (convertita da minuti a ore)
    const netHours = totalHours - (lunchBreakMinutes / 60);
    return Math.max(0, netHours); // Non può essere negativo
  };

  const buildLeaveMap = (leaves: any[]): Map<string, LeaveSummary[]> => {
    const leaveMap = new Map<string, LeaveSummary[]>();
    leaves.forEach((entry: any) => {
      const rawDate: string | undefined = entry?.absence_date || entry?.absenceDate;
      if (!rawDate || typeof rawDate !== 'string') {
        return;
      }
      const dateKey = rawDate.split('T')[0];
      const rawType = entry?.absence_type || entry?.absenceType || '';
      const hoursValue = entry?.hours_absent ?? entry?.hoursAbsent ?? null;
      let hours: number | null = null;
      if (typeof hoursValue === 'number') {
        hours = hoursValue;
      } else if (typeof hoursValue === 'string') {
        const parsed = parseFloat(hoursValue);
        if (!Number.isNaN(parsed)) {
          hours = parsed;
        }
      }
      const leaveReason = entry?.leave_reason || entry?.leaveReason || null;
      const leaveRequestId = entry?.leave_request_id || entry?.leaveRequestID || null;
      const summary: LeaveSummary = { 
        type: String(rawType), 
        hours, 
        leave_reason: leaveReason,
        leave_request_id: leaveRequestId ? Number(leaveRequestId) : undefined
      };
      const list = leaveMap.get(dateKey) ?? [];
      list.push(summary);
      leaveMap.set(dateKey, list);
    });
    return leaveMap;
  };

  const buildDayRecords = (
    selectedYear: number,
    selectedMonth: number,
    history: any[],
    leaveMap: Map<string, LeaveSummary[]>,
    expectedDaily: Record<string, number>
  ): DayRecord[] => {
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const dayRecords: DayRecord[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = dayNames[date.getDay()];
      const isWeekend = isItalianHoliday(date);

      const presence = history.find((p: any) => {
        const checkIn = new Date(p.check_in);
        return (
          checkIn.getDate() === day &&
          checkIn.getMonth() === selectedMonth - 1 &&
          checkIn.getFullYear() === selectedYear
        );
      });

      const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const leaveSummaries = leaveMap.get(dateKey) || [];

      let status: DayRecord['status'] = isWeekend ? 'holiday' : 'absence';
      let check_in1: string | null = null;
      let check_out1: string | null = null;
      let check_in2: string | null = null;
      let check_out2: string | null = null;
      let total_hours: number | null = null;
      let presenceId: number | undefined;
      let lastAdminUpdateAt: string | undefined;
      let lastAdminFullName: string | undefined;
      let lastAdminNote: string | null | undefined;
      let checkInRaw: string | null | undefined = presence?.check_in ?? null;
      let checkOutRaw: string | null | undefined = presence?.check_out ?? null;
      const modificationsRaw = Array.isArray(presence?.modifications) ? presence.modifications : [];
      const modifications: PresenceModificationRow[] = modificationsRaw.map((mod: any) => {
        const adminName = [mod.admin_first_name, mod.admin_last_name].filter(Boolean).join(' ');
        const parseHours = (value: any) => {
          if (value == null) return null;
          const parsed = typeof value === 'number' ? value : parseFloat(String(value));
          return Number.isNaN(parsed) ? null : parsed;
        };
        return {
          id: mod.id,
          adminName: adminName || 'Admin',
          createdAt: mod.created_at,
          oldCheckIn: mod.old_check_in,
          newCheckIn: mod.new_check_in,
          oldCheckOut: mod.old_check_out,
          newCheckOut: mod.new_check_out,
          oldWorkHours: parseHours(mod.old_work_hours),
          newWorkHours: parseHours(mod.new_work_hours),
          adminNote: mod.admin_note ?? null,
        };
      });

      if (presence) {
        presenceId = presence.id;
        check_in1 = presence.check_in
          ? new Date(presence.check_in).toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : null;
        check_out1 = presence.check_out
          ? new Date(presence.check_out).toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : null;
        const numericWorkHours =
          typeof presence.work_hours === 'number'
            ? presence.work_hours
            : presence.work_hours != null
            ? Number(presence.work_hours)
            : null;
        total_hours =
          numericWorkHours != null && !Number.isNaN(numericWorkHours)
            ? numericWorkHours
            : presence.total_hours ?? 0;

        if (presence.check_out) {
          status = isWeekend ? 'holiday' : 'complete';
        } else if (presence.check_in) {
          status = 'incomplete';
        }

        if (presence.last_admin_update_at) {
          lastAdminUpdateAt = presence.last_admin_update_at;
        }
        if (presence.last_admin_first_name || presence.last_admin_last_name) {
          lastAdminFullName = [presence.last_admin_first_name, presence.last_admin_last_name]
            .filter(Boolean)
            .join(' ');
        }
        if (presence.last_admin_note != null) {
          lastAdminNote = presence.last_admin_note;
        }
      }

      const expectedDayHours =
        typeof expectedDaily[dateKey] === 'number' ? expectedDaily[dateKey] : null;

      if (leaveSummaries.length > 0 && presence == null) {
        status = 'leave';
      }

      dayRecords.push({
        presenceId,
        date: dateKey,
        dayOfWeek,
        check_in1,
        check_out1,
        check_in2,
        check_out2,
        total_hours,
        status,
        notes: presence?.notes || null,
        leaves: leaveSummaries.length > 0 ? leaveSummaries : undefined,
        expectedHours: expectedDayHours,
        lastAdminUpdateAt,
        lastAdminFullName,
        lastAdminNote,
        wasModified: Boolean(lastAdminUpdateAt || (modifications && modifications.length > 0)),
        checkInRaw,
        checkOutRaw,
        modifications,
      });
    }

    return dayRecords;
  };

  const formatTime = (iso?: string | null) => {
    if (!iso) return '-';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatHours = (value?: number | null) =>
    value != null && !Number.isNaN(value) ? `${value.toFixed(2)}h` : '-';

  const buildExpectedDailyMap = (raw: any): Record<string, number> => {
    const result: Record<string, number> = {};
    if (raw && typeof raw === 'object') {
      Object.entries(raw).forEach(([key, value]) => {
        const numericValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (!Number.isNaN(numericValue)) {
          result[key] = numericValue;
        }
      });
    }
    return result;
  };

  const computePendingFromHistory = (
    history: any[],
    leaves: any[],
    expectedDaily: Record<string, number>,
    selectedYear: number,
    selectedMonth: number,
    tolerance = 0.1
  ) => {
    const leaveMap = buildLeaveMap(leaves);
    const presenceByDate = new Map<string, any>();
    if (Array.isArray(history)) {
      history.forEach((presence: any) => {
        const checkInValue = presence?.check_in;
        if (!checkInValue) return;
        const checkInDate = new Date(checkInValue);
        if (Number.isNaN(checkInDate.getTime())) return;
        const dateKey = `${checkInDate.getFullYear()}-${String(checkInDate.getMonth() + 1).padStart(2, '0')}-${String(
          checkInDate.getDate()
        ).padStart(2, '0')}`;
        const existing = presenceByDate.get(dateKey);
        if (!existing || (!existing.check_out && presence.check_out)) {
          presenceByDate.set(dateKey, presence);
        }
      });
    }

    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const todayISO = getLocalDateKey();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isWeekend = isItalianHoliday(date);

      const presence = presenceByDate.get(dateKey);
      const leaveSummaries = leaveMap.get(dateKey) || [];

      let status: DayRecord['status'] = isWeekend ? 'holiday' : 'absence';
      let totalHours = 0;

      if (presence) {
        const numericWorkHours =
          typeof presence.work_hours === 'number'
            ? presence.work_hours
            : presence.work_hours != null
            ? Number(presence.work_hours)
            : null;

        if (numericWorkHours != null && !Number.isNaN(numericWorkHours)) {
          totalHours = numericWorkHours;
        } else if (typeof presence.total_hours === 'number') {
          totalHours = presence.total_hours;
        } else if (presence.total_hours != null) {
          const parsed = Number(presence.total_hours);
          if (!Number.isNaN(parsed)) {
            totalHours = parsed;
          }
        }

        if (presence.check_out) {
          status = isWeekend ? 'holiday' : 'complete';
        } else if (presence.check_in) {
          status = 'incomplete';
        }
      }

      if (leaveSummaries.length > 0 && !presence) {
        status = 'leave';
      }

      const expectedHours = expectedDaily[dateKey];
      if (status === 'holiday' || status === 'leave') {
        continue;
      }
      if (expectedHours == null) {
        continue;
      }
      if (dateKey > todayISO) {
        continue;
      }

      if (totalHours + tolerance < expectedHours) {
        return true;
      }
    }

    return false;
  };



  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const loadGroups = async () => {
      try {
        const response = isSuperAdmin
          ? await adminGroupsAPI.getAllGroups()
          : await adminGroupsAPI.getMyGroups();
        const groupsList = response.groups || [];
        const groupsData = groupsList.map((g: any) => ({ id: g.id, name: g.name }));
        setGroups(groupsData);
        return groupsData;
      } catch (err) {
        setGroups([]);
        return [];
      }
    };

    const loadEmployees = async () => {
      setLoadingEmployees(true);
      try {
        // Carica prima i gruppi per avere la mappa completa
        const groupsData = await loadGroups();
        
        const [employeesResponse, usersResponse] = await Promise.all([
          employeeAPI.searchEmployees({
            stato: 'attivo',
            limit: 10000, // Aumentato a 10000 per includere tutti i dipendenti attivi
          }),
          debugAPI.getUsers(),
        ]);
        
        const activeUsersRaw = usersResponse?.users || usersResponse || [];
        const activeUsers = Array.isArray(activeUsersRaw)
          ? activeUsersRaw.filter((u: any) => u && u.active !== false)
          : [];
        const activeUserIds = new Set(activeUsers.map((u: any) => u.id));
        
        // Per gli admin di gruppo, non filtrare per utente attivo - devono vedere tutti i dipendenti assegnati
        // Per i super admin, filtrare solo per dipendenti con utente attivo (comportamento normale)
        const shouldFilterByActiveUser = isSuperAdmin;

        // Crea una mappa employee_id -> gruppo (carica i gruppi per ogni dipendente)
        const employeeGroupsMap = new Map<number, { id: number; name: string }>();
        if (groupsData.length > 0) {
          try {
            const groupEmployeesPromises = groupsData.map(async (group) => {
              try {
                const response = isSuperAdmin
                  ? await adminGroupsAPI.getGroupEmployees(group.id)
                  : await adminGroupsAPI.getMyGroupEmployees(group.id);
                const employees = response.employees || response || [];
                employees.forEach((emp: any) => {
                  // Se un dipendente è in più gruppi, prendi il primo trovato
                  if (!employeeGroupsMap.has(emp.id)) {
                    employeeGroupsMap.set(emp.id, group);
                  }
                });
              } catch (err) {}
            });
            await Promise.all(groupEmployeesPromises);
          } catch (err) {}
        }

        const items = (employeesResponse.employees || [])
          .filter((emp: any) => {
            // Filtra sempre per stato dipendente (deve essere attivo o vuoto)
            const stato = (emp.stato || '').toLowerCase();
            const isEmployeeActive = stato === '' || stato === 'attivo';
            if (!isEmployeeActive) {
              return false;
            }
            
            // Per gli admin di gruppo, mostra tutti i dipendenti assegnati anche se l'utente non è attivo
            // Per i super admin, filtra solo per dipendenti con utente attivo
            if (shouldFilterByActiveUser) {
              if (!emp.user_id) {
                return false;
              }
              const linkedUserActive = activeUserIds.has(emp.user_id);
              if (!linkedUserActive) {
                return false;
              }
            }
            
            return true;
          })
          .map((emp: any) => {
            const group = employeeGroupsMap.get(emp.id);
            const employeeOption = {
              employeeId: emp.id,
              userId: emp.user_id || null, // Permetti null per admin di gruppo
              fullName: `${emp.nome || ''} ${emp.cognome || ''}`.trim(),
              matricola: emp.matricola,
              dipartimento: emp.dipartimento,
              sede: emp.sede,
              weeklyHours: typeof emp.ore_settimanali === 'number' ? emp.ore_settimanali : null,
              weeklyDays: typeof emp.giorni_settimanali === 'number' ? emp.giorni_settimanali : null,
              pausaPranzo: typeof emp.pausa_pranzo === 'number' ? emp.pausa_pranzo : 60, // Default 60 minuti
              groupId: group?.id || null,
              groupName: group?.name || null,
            };
            return employeeOption;
          });

        items.sort((a, b) => a.fullName.localeCompare(b.fullName, 'it-IT'));
        
        // Per gli admin di gruppo, mostra il numero di dipendenti assegnati
        // Per i super admin, mostra il numero totale di utenti attivi
        if (isSuperAdmin) {
          setActiveUsersCount(activeUsers.length);
        } else {
          // Per admin di gruppo, mostra il numero di dipendenti disponibili
          setActiveUsersCount(items.length);
        }
        
        setEmployees(items);
        if (items.length > 0) {
          // Per gli admin di gruppo, seleziona il primo dipendente anche se non ha user_id
          // (ma non possiamo caricare il cartellino senza user_id)
          const firstEmployee = items[0];
          if (firstEmployee.userId) {
            setSelectedUserId(firstEmployee.userId);
            setSelectedEmployee(firstEmployee);
          } else {
            // Dipendente senza user_id - non possiamo caricare il cartellino
            setSelectedUserId(null);
            setSelectedEmployee(firstEmployee);
          }
        } else {
          setSelectedUserId(null);
          setSelectedEmployee(null);
        }

      } catch (err) {
        // Errore caricamento dipendenti attivi
        setError('Errore nel caricamento dei dipendenti attivi');
        setPendingFlags({});
      } finally {
        setLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [user?.role, isSuperAdmin]);

  useEffect(() => {
    if (!selectedUserId) {
      setValidationPreview(null);
      return;
    }
    loadTimesheet(selectedUserId, year, month);
    // Carica anche la preview della validazione
    loadValidationPreview(selectedUserId, year, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, year, month]);

  useEffect(() => {
    const state = location.state as
      | {
          focusEmployeeId?: number;
          focusUserId?: number;
          focusYear?: number;
          focusMonth?: number;
        }
      | null;

    if (!state || employees.length === 0) {
      return;
    }

    const targetEmployee = employees.find((employee) => {
      if (state.focusEmployeeId && employee.employeeId === state.focusEmployeeId) {
        return true;
      }
      if (state.focusUserId && employee.userId === state.focusUserId) {
        return true;
      }
      return false;
    });

    if (targetEmployee) {
      setSelectedEmployee(targetEmployee);
      setSelectedUserId(targetEmployee.userId || null);
    }

    if (typeof state.focusYear === 'number') {
      setYear(state.focusYear);
    }
    if (typeof state.focusMonth === 'number') {
      setMonth(state.focusMonth);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [employees, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (employees.length === 0) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const focusEmployeeId = Number(params.get('employee_id'));
    const focusUserId = Number(params.get('user_id'));
    const focusYear = Number(params.get('year'));
    const focusMonth = Number(params.get('month'));

    const hasEmployeeId = Number.isFinite(focusEmployeeId) && focusEmployeeId > 0;
    const hasUserId = Number.isFinite(focusUserId) && focusUserId > 0;

    if (!hasEmployeeId && !hasUserId && !params.has('year') && !params.has('month')) {
      return;
    }

    const targetEmployee = employees.find((employee) => {
      if (hasEmployeeId && employee.employeeId === focusEmployeeId) {
        return true;
      }
      if (hasUserId && employee.userId === focusUserId) {
        return true;
      }
      return false;
    });

    if (targetEmployee) {
      if (selectedEmployee?.employeeId !== targetEmployee.employeeId) {
        setSelectedEmployee(targetEmployee);
      }
      if (selectedUserId !== (targetEmployee.userId || null)) {
        setSelectedUserId(targetEmployee.userId || null);
      }
    }

    if (Number.isFinite(focusYear) && focusYear > 0 && year !== focusYear) {
      setYear(focusYear);
    }
    if (Number.isFinite(focusMonth) && focusMonth >= 1 && focusMonth <= 12 && month !== focusMonth) {
      setMonth(focusMonth);
    }
  }, [employees, location.search, month, selectedEmployee?.employeeId, selectedUserId, year]);

  const contextParams = new URLSearchParams(location.search);
  const hasTimesheetContext =
    contextParams.has('employee_id') ||
    contextParams.has('user_id') ||
    contextParams.has('year') ||
    contextParams.has('month');

  const clearTimesheetContext = () => {
    const now = new Date();
    const firstEmployee = employees[0] || null;

    setEmployeeSearchText('');
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedEmployee(firstEmployee);
    setSelectedUserId(firstEmployee?.userId || null);

    navigate(location.pathname, { replace: true });
  };

  useEffect(() => {
    if (employees.length === 0) {
      setPendingFlags({});
      setValidatedFlags({});
      return;
    }
    let cancelled = false;
    const updateFlags = async () => {
      const [pendingMap, validatedMap] = await Promise.all([
        refreshPendingFlagsForEmployees(employees, year, month),
        loadValidatedFlags(employees, year, month),
      ]);
      if (!cancelled) {
        setPendingFlags(pendingMap);
        setValidatedFlags(validatedMap);
      }
    };
    updateFlags();
    return () => {
      cancelled = true;
    };
  }, [employees, year, month, refreshPendingFlagsForEmployees, loadValidatedFlags]);

  const loadTimesheet = async (userId: number, selectedYear: number, selectedMonth: number) => {
    try {
      setLoadingTimesheet(true);
      setError(null);
      setEditingPresenceId(null);
      setEditingRecord(null);
      setEditedCheckIn('');
      setEditedCheckOut('');
      setEditedAdminNote('');
      setEditingExpectedDate(null);
      setEditedExpectedHours('');

      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;

      const data = await checkoutAdminAPI.getUserHistory(userId, startDate, endDate);
      const history = Array.isArray(data.history) ? data.history : [];
      const leaves = Array.isArray(data.leaves) ? data.leaves : [];
      const expected = typeof data.expected_hours === 'number' ? data.expected_hours : null;
      const expectedDaily = buildExpectedDailyMap(data.expected_hours_per_day);
      const leaveMap = buildLeaveMap(leaves);
      const hasPendingDays = computePendingFromHistory(history, leaves, expectedDaily, selectedYear, selectedMonth);
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      const dayRecords: DayRecord[] = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(selectedYear, selectedMonth - 1, day);
        const dayOfWeek = dayNames[date.getDay()];
        const isWeekend = isItalianHoliday(date);

        const presence = history.find((p: any) => {
          const checkIn = new Date(p.check_in);
          return (
            checkIn.getDate() === day &&
            checkIn.getMonth() === selectedMonth - 1 &&
            checkIn.getFullYear() === selectedYear
          );
        });

        const dateKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const leaveSummaries = leaveMap.get(dateKey) || [];

        let status: DayRecord['status'] = isWeekend ? 'holiday' : 'absence';
        let check_in1: string | null = null;
        let check_out1: string | null = null;
        let check_in2: string | null = null;
        let check_out2: string | null = null;
        let total_hours: number | null = null;
        let presenceId: number | undefined;
        let lastAdminUpdateAt: string | undefined;
        let lastAdminFullName: string | undefined;
        let lastAdminNote: string | null | undefined;
        let checkInRaw: string | null | undefined = presence?.check_in ?? null;
        let checkOutRaw: string | null | undefined = presence?.check_out ?? null;
        const modificationsRaw = Array.isArray(presence?.modifications) ? presence.modifications : [];
        const modifications: PresenceModificationRow[] = modificationsRaw.map((mod: any) => {
          const adminName = [mod.admin_first_name, mod.admin_last_name].filter(Boolean).join(' ');
          const parseHours = (value: any) => {
            if (value == null) return null;
            const parsed = typeof value === 'number' ? value : parseFloat(String(value));
            return Number.isNaN(parsed) ? null : parsed;
          };
          return {
            id: mod.id,
            adminName: adminName || 'Admin',
            createdAt: mod.created_at,
            oldCheckIn: mod.old_check_in,
            newCheckIn: mod.new_check_in,
            oldCheckOut: mod.old_check_out,
            newCheckOut: mod.new_check_out,
            oldWorkHours: parseHours(mod.old_work_hours),
            newWorkHours: parseHours(mod.new_work_hours),
            adminNote: mod.admin_note ?? null,
          };
        });

        if (presence) {
          presenceId = presence.id;
          check_in1 = presence.check_in
            ? new Date(presence.check_in).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
              })
            : null;
          check_out1 = presence.check_out
            ? new Date(presence.check_out).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
              })
            : null;
          const numericWorkHours =
            typeof presence.work_hours === 'number'
              ? presence.work_hours
              : presence.work_hours != null
              ? Number(presence.work_hours)
              : null;
          total_hours =
            numericWorkHours != null && !Number.isNaN(numericWorkHours)
              ? numericWorkHours
              : presence.total_hours ?? 0;

          if (presence.check_out) {
            status = isWeekend ? 'holiday' : 'complete';
          } else if (presence.check_in) {
            status = 'incomplete';
          }

          if (presence.last_admin_update_at) {
            lastAdminUpdateAt = presence.last_admin_update_at;
          }
          const adminNameParts = [presence.last_admin_first_name, presence.last_admin_last_name].filter(Boolean);
          if (adminNameParts.length) {
            lastAdminFullName = adminNameParts.join(' ');
          }
          lastAdminNote = presence.last_admin_note ?? null;
        }

        const expectedDayHours =
          typeof expectedDaily[dateKey] === 'number' ? expectedDaily[dateKey] : null;

        // Calcola ore permesso per questo giorno
        const permissionHours = leaveSummaries
          .filter(leave => leave.type === 'permesso')
          .reduce((sum, leave) => sum + (leave.hours || 0), 0);

        // QUADRATURA: Ore totali giornaliere = ore lavorate + ore permesso
        const totalDayHours = (total_hours || 0) + permissionHours;

        if (leaveSummaries.length > 0 && presence == null) {
          status = 'leave';
        }

        dayRecords.push({
          presenceId,
          date: dateKey,
          dayOfWeek,
          check_in1,
          check_out1,
          check_in2,
          check_out2,
          total_hours: totalDayHours, // Includi ore permesso nelle ore totali
          status,
          notes: presence?.notes || null,
          leaves: leaveSummaries.length > 0 ? leaveSummaries : undefined,
          expectedHours: expectedDayHours,
          lastAdminUpdateAt,
          lastAdminFullName,
          lastAdminNote,
          wasModified: Boolean(lastAdminUpdateAt || (modifications && modifications.length > 0)),
          checkInRaw,
          checkOutRaw,
          modifications,
        });
      }

      setRecords(dayRecords);
      // Le ore totali mensili includono già i permessi (calcolati sopra)
      const hours = dayRecords.reduce((sum, day) => sum + (day.total_hours || 0), 0);
      const completeDays = dayRecords.filter((day) => day.status === 'complete').length;
      setTotalHours(hours);
      setWorkingDays(completeDays);
      setExpectedHours(expected);
      setExpectedHoursPerDay(expectedDaily);
      setPendingFlags((prev) => {
        if (prev[userId] === hasPendingDays) {
          return prev;
        }
        return { ...prev, [userId]: hasPendingDays };
      });
    } catch (err) {
      // Errore caricamento cartellino admin
      setError('Errore nel caricamento del cartellino selezionato');
      setRecords([]);
      setTotalHours(0);
      setWorkingDays(0);
      setExpectedHours(null);
      setExpectedHoursPerDay({});
    } finally {
      setLoadingTimesheet(false);
    }
  };

  const handlePrint = () => window.print();

  // Gestione permessi
  const handleOpenLeaveDialog = (date: string, record?: { date: string; expectedHours?: number | null }) => {
    if (record && isRecordDateHoliday(record)) return;
    setLeaveDialogDate(date);
    setLeaveDialogType('permesso');
    setLeaveDialogHours(0);
    // Ore attese del giorno (per max permesso): da record o da dipendente (weeklyHours/weeklyDays)
    const expectedFromRecord = record?.expectedHours ?? null;
    const expectedFromEmp =
      selectedEmployee?.weeklyHours && selectedEmployee?.weeklyDays
        ? selectedEmployee.weeklyHours / selectedEmployee.weeklyDays
        : 8;
    setLeaveDialogExpectedHours(
      expectedFromRecord != null && expectedFromRecord > 0 ? expectedFromRecord : expectedFromEmp
    );
    setLeaveDialogReason('');
    setLeaveDialogNotes('');
    setLeaveDialogOpen(true);
  };

  const handleCloseLeaveDialog = () => {
    setLeaveDialogOpen(false);
    setLeaveDialogDate(null);
    setLeaveDialogType('permesso');
    setLeaveDialogHours(0);
    setLeaveDialogReason('');
    setLeaveDialogNotes('');
  };

  const handleSaveLeave = async () => {
    if (!selectedEmployee || !leaveDialogDate) return;

    // Validazione
    if (leaveDialogType === 'permesso' && !leaveDialogReason) {
      setError('La causale è obbligatoria per i permessi');
      return;
    }

    if (leaveDialogType === 'permesso' && leaveDialogHours <= 0) {
      setError('Inserisci le ore del permesso');
      return;
    }

    setSavingLeave(true);
    setError(null);

    try {
      const requestData: any = {
        request_type: leaveDialogType,
        start_date: leaveDialogDate,
        notes: leaveDialogNotes || undefined,
      };

      if (leaveDialogType === 'permesso') {
        requestData.duration_hours = leaveDialogHours;
        requestData.leave_reason = leaveDialogReason;
      } else {
        // Per ferie/malattia, usa la stessa data come end_date
        requestData.end_date = leaveDialogDate;
      }

      await leaveAdminAPI.createLeaveForEmployee(selectedEmployee.employeeId, requestData);

      // Ricarica il cartellino
      if (selectedUserId) {
        await loadTimesheet(selectedUserId, year, month);
      }

      handleCloseLeaveDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'inserimento del permesso');
    } finally {
      setSavingLeave(false);
    }
  };

  const handleDeleteLeave = async (leaveRequestId: number) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo permesso/ferie?')) {
      return;
    }

    setDeletingLeave(leaveRequestId);
    setError(null);

    try {
      await leaveAdminAPI.deleteLeave(leaveRequestId);

      // Ricarica il cartellino
      if (selectedUserId) {
        await loadTimesheet(selectedUserId, year, month);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore nell\'eliminazione del permesso');
    } finally {
      setDeletingLeave(null);
    }
  };

  const loadValidationPreview = async (userId: number, selectedYear: number, selectedMonth: number) => {
    if (!token) return;
    
    setLoadingValidation(true);
    try {
      // Usa l'API helper invece di fetch diretto per assicurarsi che l'header X-Impersonate-User-ID venga aggiunto
      const data = await validationAPI.getValidationPreview(selectedYear, selectedMonth, userId);
      setValidationPreview(data);
    } catch (err: any) {
      // Ignora errori di validazione (non bloccanti)
      setValidationPreview(null);
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleOpenValidationDialog = async () => {
    if (!selectedUserId) return;
    
    // Carica la preview se non è già caricata
    if (!validationPreview) {
      await loadValidationPreview(selectedUserId, year, month);
    }
    
    setValidationDialogOpen(true);
  };

  const handleValidatePresence = async () => {
    if (!selectedUserId || !validationPreview) return;

    setLoadingValidation(true);
    setError(null);
    try {
      const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || (runtimeOrigin ? `${runtimeOrigin}/api` : 'http://localhost:8080/api');
      const response = await fetch(
        `${API_BASE_URL}/presence/validation/validate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user_id: selectedUserId,
            year,
            month,
            validation_notes: validationNotes || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore nella validazione');
      }

      setValidationDialogOpen(false);
      setValidationNotes('');
      setValidationPreview(null);
      // Ricarica il cartellino
      if (selectedUserId) {
        loadTimesheet(selectedUserId, year, month);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleDownload = () => {
    if (!selectedEmployee || records.length === 0) {
      alert('Seleziona un dipendente e carica il cartellino prima di esportare');
      return;
    }

    // Prepara i dati per l'export
    const exportData = records.map((record) => {
      const date = new Date(record.date);
      const dayOfMonth = date.getDate();
      
      // Formatta le ferie/permessi
      let leavesInfo = '';
      if (record.leaves && record.leaves.length > 0) {
        leavesInfo = record.leaves
          .map((l) => `${l.type}: ${l.hours || 0}h`)
          .join(', ');
      }

      // Determina lo stato
      let statusText = '';
      switch (record.status) {
        case 'complete':
          statusText = 'Completo';
          break;
        case 'incomplete':
          statusText = 'Incompleto';
          break;
        case 'holiday':
          statusText = 'Festivo';
          break;
        case 'leave':
          statusText = 'Ferie/Permesso';
          break;
        case 'absence':
          statusText = 'Assenza';
          break;
        default:
          statusText = '-';
      }

      return {
        'Giorno': dayOfMonth,
        'Giorno Settimana': record.dayOfWeek,
        'Data': record.date,
        'Entrata 1': record.check_in1 || '-',
        'Uscita 1': record.check_out1 || '-',
        'Entrata 2': record.check_in2 || '-',
        'Uscita 2': record.check_out2 || '-',
        'Ore Totali': record.total_hours ? record.total_hours.toFixed(2) : '-',
        'Ore Attese': record.expectedHours ? record.expectedHours.toFixed(2) : '-',
        'Stato': statusText,
        'Ferie/Permessi': leavesInfo || '-',
        'Note': record.notes || '-',
        'Modificato': record.wasModified ? 'Sì' : 'No',
        'Ultima Modifica': record.lastAdminUpdateAt 
          ? new Date(record.lastAdminUpdateAt).toLocaleString('it-IT')
          : '-',
        'Modificato da': record.lastAdminFullName || '-',
        'Note Admin': record.lastAdminNote || '-',
      };
    });

    // Aggiungi riga di riepilogo
    const summaryRow = {
      'Giorno': 'RIEPILOGO',
      'Giorno Settimana': '',
      'Data': '',
      'Entrata 1': '',
      'Uscita 1': '',
      'Entrata 2': '',
      'Uscita 2': '',
      'Ore Totali': totalHours.toFixed(2),
      'Ore Attese': expectedHours ? expectedHours.toFixed(2) : '-',
      'Stato': '',
      'Ferie/Permessi': '',
      'Note': '',
      'Modificato': '',
      'Ultima Modifica': '',
      'Modificato da': '',
      'Note Admin': '',
    };

    // Crea il workbook
    const ws = XLSX.utils.json_to_sheet([...exportData, summaryRow]);
    const wb = XLSX.utils.book_new();
    
    // Imposta larghezza colonne
    const colWidths = [
      { wch: 8 },  // Giorno
      { wch: 12 }, // Giorno Settimana
      { wch: 12 }, // Data
      { wch: 10 }, // Entrata 1
      { wch: 10 }, // Uscita 1
      { wch: 10 }, // Entrata 2
      { wch: 10 }, // Uscita 2
      { wch: 12 }, // Ore Totali
      { wch: 12 }, // Ore Attese
      { wch: 15 }, // Stato
      { wch: 20 }, // Ferie/Permessi
      { wch: 30 }, // Note
      { wch: 10 }, // Modificato
      { wch: 20 }, // Ultima Modifica
      { wch: 20 }, // Modificato da
      { wch: 30 }, // Note Admin
    ];
    ws['!cols'] = colWidths;

    // Aggiungi informazioni del dipendente come header
    const employeeInfo = [
      ['DIPENDENTE', selectedEmployee.fullName],
      ['Matricola', selectedEmployee.matricola || '-'],
      ['Dipartimento', selectedEmployee.dipartimento || '-'],
      ['Mese', `${monthNames[month - 1]} ${year}`],
      ['Ore Settimanali', selectedEmployee.weeklyHours || '-'],
      ['Giorni Settimanali', selectedEmployee.weeklyDays || '-'],
      ['Pausa Pranzo (min)', selectedEmployee.pausaPranzo || '-'],
      [''],
      ['RIEPILOGO MENSILE'],
      ['Totale Ore', totalHours.toFixed(2)],
      ['Giorni Lavorativi', workingDays],
      ['Ore Attese', expectedHours ? expectedHours.toFixed(2) : '-'],
      [''],
    ];

    // Crea un secondo foglio con le informazioni del dipendente
    const infoWs = XLSX.utils.aoa_to_sheet(employeeInfo);
    XLSX.utils.book_append_sheet(wb, infoWs, 'Info Dipendente');
    XLSX.utils.book_append_sheet(wb, ws, 'Cartellino');

    // Genera il nome file
    const employeeName = selectedEmployee.fullName.replace(/\s+/g, '_');
    const filename = `Cartellino_${employeeName}_${monthNames[month - 1]}_${year}.xlsx`;

    // Scarica il file
    XLSX.writeFile(wb, filename);
  };

  const handleDownloadINPAS = () => {
    if (!selectedEmployee || records.length === 0) {
      alert('Seleziona un dipendente e carica il cartellino prima di esportare');
      return;
    }

    const wb = XLSXStyle.utils.book_new();
    
    // Calcola i giorni del mese
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Prepara i dati per il formato INPAS
    const inpasData: any[][] = [];
    
    // Header: Logo INPAS, Mese, Anno, Nominativo e Sede
    inpasData.push(['inpas', '', '', '', '', '', '']); // Riga 1: Logo
    inpasData.push(['Mese', monthNames[month - 1], '', 'Anno', year.toString(), '', '']); // Riga 2: Mese e Anno
    inpasData.push(['Nominativo e Sede', `${selectedEmployee.fullName.toUpperCase()}${selectedEmployee.sede ? ` - ${selectedEmployee.sede}` : ''}`, '', '', '', '', '']); // Riga 3: Nominativo e Sede
    inpasData.push([]); // Riga vuota
    
    // Header tabella
    inpasData.push(['giorno', 'ore presenza', 'ore ferie', 'ore permesso', 'altre assenze', '', '']); // Header principale
    inpasData.push(['', '', '', '', 'n.ore', 'motivazione', '']); // Sotto-header per "altre assenze"
    
    // Dati giornalieri
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const record = records.find(r => r.date === dateKey);
      const date = new Date(year, month - 1, day);
      const isWeekend = isItalianHoliday(date); // Domenica o Sabato
      
      let orePresenza = '';
      let oreFerie = '';
      let orePermesso = '';
      let altreAssenzeOre = '';
      let altreAssenzeMotivazione = '';
      
      if (record) {
        // Calcola ore presenza (lavorate, escludendo permessi)
        const permissionHours = record.leaves?.reduce((sum, leave) => {
          if (leave.type === 'permesso' && leave.hours) {
            return sum + leave.hours;
          }
          return sum;
        }, 0) ?? 0;
        
        let workHours = (record.total_hours || 0) - permissionHours;
        
        // Se lo stato è "completo" e ci sono ore previste, limita le ore presenza al valore previsto
        // (le ore in più non devono essere inserite)
        if (record.status === 'complete' && record.expectedHours != null && !Number.isNaN(record.expectedHours)) {
          // Limita le ore lavorate alle ore previste (escludendo i permessi)
          const maxWorkHours = record.expectedHours - permissionHours;
          if (workHours > maxWorkHours) {
            workHours = Math.max(0, maxWorkHours);
          }
        }
        
        if (workHours > 0) {
          // Arrotonda matematicamente: 6.98 = 7, 4.3 = 4
          orePresenza = Math.round(workHours).toString();
        }
        
        // Calcola ferie
        const ferieHours = record.leaves?.reduce((sum, leave) => {
          if (leave.type === 'ferie' && leave.hours) {
            return sum + leave.hours;
          }
          return sum;
        }, 0) ?? 0;
        if (ferieHours > 0) {
          // Arrotonda matematicamente
          oreFerie = Math.round(ferieHours).toString();
        }
        
        // Calcola permessi con causale
        if (permissionHours > 0) {
          const permessi = record.leaves?.filter(leave => leave.type === 'permesso') || [];
          const permessoArrotondato = Math.round(permissionHours);
          
          // Se c'è una causale, aggiungila
          if (permessi.length > 0 && permessi[0].leave_reason) {
            orePermesso = `${permessoArrotondato} - ${permessi[0].leave_reason}`;
          } else {
            orePermesso = permessoArrotondato.toString();
          }
        }
        
        // Altre assenze (malattia, altri)
        const altreAssenze = record.leaves?.filter(leave => 
          leave.type === 'malattia' || leave.type === 'altri'
        ) || [];
        
        if (altreAssenze.length > 0) {
          const altreOre = altreAssenze.reduce((sum, leave) => sum + (leave.hours || 0), 0);
          // Arrotonda matematicamente
          altreAssenzeOre = Math.round(altreOre).toString();
          // Prendi la prima motivazione disponibile o il tipo
          const primaAssenza = altreAssenze[0];
          if (primaAssenza.type === 'malattia') {
            altreAssenzeMotivazione = primaAssenza.leave_reason || 'MALATTIA';
          } else {
            altreAssenzeMotivazione = primaAssenza.leave_reason || primaAssenza.type.toUpperCase();
          }
        }
      }
      
      inpasData.push([
        day.toString(),
        orePresenza,
        oreFerie,
        orePermesso,
        altreAssenzeOre,
        altreAssenzeMotivazione,
        ''
      ]);
    }
    
    // Riga vuota
    inpasData.push([]);
    
    // Footer: Nota e Firma Operatore
    inpasData.push(['Ai fini della validità del documento è necessario che siano presenti le firme richieste in calce.', '', '', '', '', '', '']);
    inpasData.push([]);
    inpasData.push(['Firma Operatore', '', '', '', 'note', '', '']);
    inpasData.push(['', '', '', '', '', '', '']); // Spazio per firma
    inpasData.push(['', '', '', '', '', '', '']); // Spazio per note
    
    // Crea il foglio di lavoro
    const ws = XLSXStyle.utils.aoa_to_sheet(inpasData);
    
    // Colore azzurro chiaro per gli header (RGB: B3D9FF)
    const lightBlueFill = {
      fgColor: { rgb: 'B3D9FF' }
    };
    
    const headerStyle = {
      fill: lightBlueFill,
      font: { bold: true, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };
    
    const defaultStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };
    
    const greyFill = {
      fgColor: { rgb: 'D3D3D3' } // Grigio chiaro per i weekend
    };
    
    // Applica stili alle celle
    const range = XLSXStyle.utils.decode_range(ws['!ref'] || 'A1');
    
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellAddress = XLSXStyle.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { v: '', t: 's' };
        }
        
        // Riga 1 (A1): Logo "inpas"
        if (R === 0 && C === 0) {
          ws[cellAddress].s = {
            ...headerStyle,
            font: { bold: true, size: 14, color: { rgb: '000080' } } // Blu scuro per il logo
          };
        }
        // Riga 2 (A2, B2, D2, E2): Mese e Anno
        else if (R === 1 && (C === 0 || C === 1 || C === 3 || C === 4)) {
          ws[cellAddress].s = headerStyle;
        }
        // Riga 3 (A3, B3): Nominativo e Sede
        else if (R === 2 && (C === 0 || C === 1)) {
          ws[cellAddress].s = headerStyle;
        }
        // Righe 5-6: Header tabella (giorno, ore presenza, ecc.)
        else if ((R === 4 || R === 5) && C <= 5) {
          ws[cellAddress].s = headerStyle;
        }
        // Righe dati (giorni del mese): Colonna giorno (C=0) con sfondo grigio per weekend
        else if (R >= 6 && R < 6 + daysInMonth && C === 0) {
          const dayNum = R - 5;
          const date = new Date(year, month - 1, dayNum);
          const isWeekend = isItalianHoliday(date);
          ws[cellAddress].s = {
            ...defaultStyle,
            fill: isWeekend ? greyFill : undefined
          };
        }
        // Righe dati: altre colonne
        else if (R >= 6 && R < 6 + daysInMonth && C <= 6) {
          ws[cellAddress].s = defaultStyle;
        }
        // Footer: "Firma Operatore" e "note" (riga prima dell'ultima)
        else if (R === range.e.r - 2 && (C === 0 || C === 4)) {
          ws[cellAddress].s = headerStyle;
        }
        // Altre celle
        else {
          ws[cellAddress].s = defaultStyle;
        }
      }
    }
    
    // Imposta larghezza colonne
    ws['!cols'] = [
      { wch: 8 },  // giorno
      { wch: 12 }, // ore presenza
      { wch: 10 }, // ore ferie
      { wch: 25 }, // ore permesso (più larga per includere la causale)
      { wch: 10 }, // n.ore (altre assenze)
      { wch: 25 }, // motivazione
      { wch: 10 }, // colonna vuota
    ];
    
    // Imposta altezza righe (opzionale, per migliorare la leggibilità)
    ws['!rows'] = [];
    for (let R = 0; R <= range.e.r; R++) {
      ws['!rows'][R] = { hpt: 20 }; // Altezza standard
      if (R === 4 || R === 5) {
        ws['!rows'][R] = { hpt: 25 }; // Header tabella più alto
      }
    }
    
    // Unisci celle per "altre assenze" (riga 5, colonne E e F)
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: 4, c: 4 }, e: { r: 4, c: 5 } }); // "altre assenze" unisce E5 e F5
    
    // Aggiungi il foglio al workbook
    XLSXStyle.utils.book_append_sheet(wb, ws, 'Cartellino INPAS');
    
    // Genera il nome file
    const employeeName = selectedEmployee.fullName.replace(/\s+/g, '_');
    const filename = `Cartellino_INPAS_${employeeName}_${monthNames[month - 1]}_${year}.xlsx`;
    
    // Scarica il file
    XLSXStyle.writeFile(wb, filename);
  };

  const handleStartEditPresence = (record: DayRecord) => {
    if (isRecordDateHoliday(record)) {
      return; // Non consentire modifica/inserimento check-in/check-out nei giorni festivi
    }
    setEditingPresenceId(record.presenceId ?? null);
    setEditingRecord(record);
    setEditedCheckIn(toTimeFieldValue(record.checkInRaw));
    setEditedCheckOut(toTimeFieldValue(record.checkOutRaw));
    setEditedAdminNote(record.lastAdminNote ?? '');
  };

  const handleCancelEditPresence = () => {
    setEditingPresenceId(null);
    setEditingRecord(null);
    setEditedCheckIn('');
    setEditedCheckOut('');
    setEditedAdminNote('');
    setSavingPresence(false);
  };

  const handleSavePresenceEdit = async () => {
    if (!selectedUserId || !editingRecord) {
      return;
    }
    if (isRecordDateHoliday(editingRecord)) {
      alert('Non è possibile inserire o modificare presenze nei giorni festivi');
      return;
    }

    const payload: Record<string, unknown> = {};
    let hasChanges = false;
    const existingPresenceId = editingRecord.presenceId ?? editingPresenceId ?? null;

    const lunchBreakMinutes = selectedEmployee?.pausaPranzo ?? 60;
    const autoHoursRaw = calculateDurationHours(editingRecord.date, editedCheckIn || null, editedCheckOut || null, lunchBreakMinutes);
    let autoHoursRounded: number | null = null;
    if (autoHoursRaw != null) {
      if (Number.isNaN(autoHoursRaw) || autoHoursRaw < 0) {
        alert('Orario di uscita deve essere successivo all\'orario di ingresso');
        return;
      }
      autoHoursRounded = Math.round(autoHoursRaw * 100) / 100;
    }

    const originalCheckInMs = editingRecord.checkInRaw
      ? new Date(editingRecord.checkInRaw).getTime()
      : null;
    const checkInTrimmed = editedCheckIn.trim();
    if (checkInTrimmed !== '') {
      const checkInIso = combineDateAndTime(editingRecord.date, checkInTrimmed);
      if (!checkInIso) {
        alert('Orario di ingresso non valido');
        return;
      }
      const newCheckInMs = new Date(checkInIso).getTime();
      if (Number.isNaN(newCheckInMs)) {
        alert('Orario di ingresso non valido');
        return;
      }
      if (originalCheckInMs == null || newCheckInMs !== originalCheckInMs) {
        payload.check_in = checkInIso;
        hasChanges = true;
      }
    }

    const originalCheckOutMs = editingRecord.checkOutRaw
      ? new Date(editingRecord.checkOutRaw).getTime()
      : null;
    const checkOutTrimmed = editedCheckOut.trim();
    if (checkOutTrimmed !== '') {
      const checkOutIso = combineDateAndTime(editingRecord.date, checkOutTrimmed);
      if (!checkOutIso) {
        alert('Orario di uscita non valido');
        return;
      }
      const newCheckOutMs = new Date(checkOutIso).getTime();
      if (Number.isNaN(newCheckOutMs)) {
        alert('Orario di uscita non valido');
        return;
      }
      if (originalCheckOutMs == null || newCheckOutMs !== originalCheckOutMs) {
        payload.check_out = checkOutIso;
        hasChanges = true;
      }
    }

    // Le ore vengono sempre calcolate automaticamente da check_in e check_out
    if (autoHoursRounded != null) {
      const originalWorkHours = editingRecord.total_hours;
      const hasDifference =
        originalWorkHours == null || Math.abs(autoHoursRounded - originalWorkHours) > 0.01;
      if (hasDifference) {
        payload.work_hours = autoHoursRounded;
        hasChanges = true;
      }
    }

    const adminNoteTrimmed = editedAdminNote.trim();
    if (adminNoteTrimmed) {
      payload.admin_note = adminNoteTrimmed;
      hasChanges = true;
    }

    if (!hasChanges) {
      alert('Nessuna modifica da salvare');
      return;
    }

    if (!existingPresenceId && !payload.check_in) {
      alert('Per creare una presenza inserisci almeno l\'orario di ingresso');
      return;
    }

    try {
      setSavingPresence(true);
      if (existingPresenceId) {
        await checkoutAdminAPI.updatePresence(existingPresenceId, payload);
      } else {
        await checkoutAdminAPI.createPresence({
          user_id: selectedUserId,
          check_in: payload.check_in as string,
          check_out: (payload.check_out as string | null | undefined) ?? null,
          work_hours: (payload.work_hours as number | null | undefined) ?? null,
          overtime_hours: (payload.overtime_hours as number | null | undefined) ?? null,
          admin_note: (payload.admin_note as string | null | undefined) ?? null,
          notes: (payload.notes as string | null | undefined) ?? null,
        });
      }
      await loadTimesheet(selectedUserId, year, month);
      handleCancelEditPresence();
    } catch (err: any) {
      // Errore aggiornamento presenza
      alert(err?.response?.data?.error || 'Errore durante l\'aggiornamento della presenza');
      setSavingPresence(false);
    }
  };

  const handleStartEditExpected = (record: DayRecord) => {
    if (isRecordDateHoliday(record)) return;
    setEditingExpectedDate(record.date);
    setEditedExpectedHours(record.expectedHours != null ? record.expectedHours.toString() : '');
  };

  const handleCancelEditExpected = () => {
    setEditingExpectedDate(null);
    setEditedExpectedHours('');
    setSavingExpected(false);
  };

  const handleSaveExpectedHours = async () => {
    if (!selectedEmployee || !editingExpectedDate) {
      return;
    }
    const parsed = parseFloat(editedExpectedHours.replace(',', '.'));
    if (Number.isNaN(parsed) || parsed < 0) {
      alert('Inserisci un valore numerico valido per le ore attese');
      return;
    }
    try {
      setSavingExpected(true);
      const response = await employeeAPI.updateExpectedHours(selectedEmployee.employeeId, {
        daily_hours: parsed,
        admin_note: 'Aggiornamento ore attese da cartellino',
      });
      if (response?.employee) {
        const updated = response.employee;
        setSelectedEmployee((prev) =>
          prev
            ? {
                ...prev,
                weeklyHours: updated.ore_settimanali ?? prev.weeklyHours,
                weeklyDays: updated.giorni_settimanali ?? prev.weeklyDays,
              }
            : prev
        );
      }
      if (selectedUserId) {
        await loadTimesheet(selectedUserId, year, month);
      }
      handleCancelEditExpected();
    } catch (err: any) {
      // Errore aggiornamento ore attese
      alert(err?.response?.data?.error || 'Errore durante l\'aggiornamento delle ore attese');
      setSavingExpected(false);
    }
  };

  const handleOpenModDialog = (record: DayRecord) => {
    if (!record.modifications || record.modifications.length === 0) {
      alert('Non ci sono modifiche registrate per questa giornata');
      return;
    }
    setModDialogData({
      date: record.date,
      modifications: record.modifications,
    });
    setModDialogOpen(true);
  };

  const handleCloseModDialog = () => {
    setModDialogOpen(false);
    setModDialogData(null);
  };

  const getStatusIcon = (status: DayRecord['status']) => {
    switch (status) {
      case 'complete':
        return <ScheduleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'incomplete':
        return <ScheduleIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'holiday':
        return <CalendarIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
    case 'leave':
      return <CalendarIcon sx={{ color: '#1976d2', fontSize: 20 }} />;
      default:
        return <span>-</span>;
    }
  };

  const getStatusColor = (status: DayRecord['status']) => {
    switch (status) {
      case 'complete':
        return 'success';
      case 'incomplete':
        return 'warning';
      case 'holiday':
        return 'default';
    case 'leave':
      return 'info';
      default:
        return 'error';
    }
  };

  const handleEmployeeChange = (event: any, newValue: EmployeeOption | null) => {
    if (newValue) {
      setSelectedUserId(newValue.userId);
      setSelectedEmployee(newValue);
      if (newValue.userId) {
        loadTimesheet(newValue.userId, year, month);
      }
    } else {
      setSelectedUserId(null);
      setSelectedEmployee(null);
    }
  };

  // Filtra e raggruppa i dipendenti per la ricerca
  const filteredAndGroupedEmployees = useMemo(() => {
    if (!employeeSearchText) {
      return employees;
    }
    const searchLower = employeeSearchText.toLowerCase();
    return employees.filter(emp => 
      emp.fullName.toLowerCase().includes(searchLower) ||
      emp.matricola?.toLowerCase().includes(searchLower) ||
      emp.groupName?.toLowerCase().includes(searchLower) ||
      emp.dipartimento?.toLowerCase().includes(searchLower)
    );
  }, [employees, employeeSearchText]);

  if (loadingGroupCheck) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="warning">Questa sezione appartiene all'area amministrativa dei cartellini.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {!isSuperAdmin && isGroupAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stai visualizzando solo i dipendenti assegnati ai tuoi gruppi admin.
        </Alert>
      )}
      {hasTimesheetContext && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearTimesheetContext}>
              Azzera contesto
            </Button>
          }
        >
          Cartellino aperto con contesto applicato dalla dashboard. Il dipendente o il periodo visualizzato possono essere stati preimpostati da un link operativo.
        </Alert>
      )}
      {employees.length === 0 && !loadingEmployees && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Nessun dipendente disponibile nel menu a tendina.
          </Typography>
          <Box component="div">
            <Typography variant="body2" component="div">
              Questo può accadere se:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 8, paddingLeft: 24 }}>
              <li>I dipendenti assegnati ai tuoi gruppi non hanno un utente associato (user_id)</li>
              <li>Gli utenti associati non sono attivi</li>
              <li>I dipendenti non sono in stato "attivo"</li>
            </ul>
            <Typography variant="body2">
              Controlla i log nella console del browser per maggiori dettagli.
            </Typography>
          </Box>
        </Alert>
      )}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1 }}>
              Cartellini Area Amministrativa
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={selectedEmployee ? 1 : 0}>
              <Chip
                label={isSuperAdmin ? `Utenti abilitati totali: ${activeUsersCount}` : `Dipendenti assegnati: ${activeUsersCount}`}
                variant="outlined"
                color="primary"
              />
              <Chip
                label={`Cartellini da completare: ${pendingCount}`}
                variant="outlined"
                color={pendingCount > 0 ? 'warning' : 'default'}
              />
            </Box>
            {selectedEmployee && (
              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 0.5, md: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {selectedEmployee.fullName}
                  {selectedEmployee.dipartimento ? ` • ${selectedEmployee.dipartimento}` : ''}
                </Typography>
                {expectedHours !== null && (
                  <Typography variant="body1" color="primary" fontWeight={600}>
                    Ore attese periodo: {expectedHours.toFixed(2)}h
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box display="flex" gap={1}>
            {(() => {
              return selectedUserId ? (
                <Tooltip title="Valida Cartellino">
                  <span>
                    <IconButton
                      onClick={handleOpenValidationDialog}
                      disabled={loadingValidation}
                      color="primary"
                      sx={{
                        opacity: loadingValidation ? 0.5 : 1,
                      }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null;
            })()}
            <Tooltip title="Aggiorna">
              <span>
                <IconButton
                  onClick={() => selectedUserId && loadTimesheet(selectedUserId, year, month)}
                  disabled={loadingTimesheet || !selectedUserId}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Stampa">
              <IconButton onClick={handlePrint}>
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Scarica Excel">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ minWidth: 140 }}
              >
                Excel
              </Button>
            </Tooltip>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  handleDownload();
                  setExportMenuAnchor(null);
                }}
              >
                <DownloadIcon sx={{ mr: 1 }} />
                Formato Standard
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDownloadINPAS();
                  setExportMenuAnchor(null);
                }}
              >
                <DownloadIcon sx={{ mr: 1 }} />
                Formato INPAS
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Grid container spacing={2} alignItems="center" mb={3}>
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={filteredAndGroupedEmployees}
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              onInputChange={(event, newInputValue) => {
                setEmployeeSearchText(newInputValue);
              }}
              loading={loadingEmployees}
              getOptionLabel={(option) => option.fullName + (option.matricola ? ` • ${option.matricola}` : '')}
              groupBy={(option) => option.groupName || 'Senza gruppo'}
              filterOptions={(options) => options} // La ricerca è già gestita da filteredAndGroupedEmployees
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Dipendente"
                  size="small"
                  placeholder={loadingEmployees ? 'Caricamento...' : 'Cerca dipendente...'}
                />
              )}
              renderOption={(props, option) => {
                const hasPending = !!pendingFlags[option.userId];
                const isValidated = !!validatedFlags[option.userId];
                return (
                  <Box component="li" {...props} key={option.userId}>
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                      <Typography component="span" sx={{ flex: 1 }}>
                        {option.fullName}
                        {option.matricola ? ` • ${option.matricola}` : ''}
                        {option.groupName && (
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            ({option.groupName})
                          </Typography>
                        )}
                      </Typography>
                      <Box display="flex" gap={0.5}>
                        {isValidated && (
                          <Chip
                            size="small"
                            label="Validato"
                            color="success"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {hasPending && (
                          <Chip
                            size="small"
                            label="Irregolare"
                            color="warning"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              }}
              isOptionEqualToValue={(option, value) => option.userId === value.userId}
              disabled={loadingEmployees || employees.length === 0}
              noOptionsText={
                loadingEmployees
                  ? 'Caricamento in corso...'
                  : activeUsersCount > 0
                  ? 'Nessun dipendente trovato'
                  : 'Nessun utente abilitato disponibile'
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Mese</InputLabel>
              <Select value={month} label="Mese" onChange={(event) => setMonth(Number(event.target.value))}>
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Anno</InputLabel>
              <Select value={year} label="Anno" onChange={(event) => setYear(Number(event.target.value))}>
                {yearOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Box display="flex" justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
              <Button
                size="medium"
                variant="contained"
                startIcon={loadingTimesheet ? <CircularProgress size={20} /> : <ScheduleIcon />}
                onClick={() => selectedUserId && loadTimesheet(selectedUserId, year, month)}
                disabled={loadingTimesheet || !selectedUserId}
                sx={{
                  px: 3,
                  py: 1,
                  backgroundColor: '#0d47a1',
                  '&:hover': { backgroundColor: '#1565c0' },
                }}
              >
                Carica
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="body2" color="text.secondary">
                Ore totali mese
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1' }}>
                {totalHours.toFixed(2)}h
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="body2" color="text.secondary">
                Giorni completi
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                {workingDays}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, backgroundColor: '#f3e5f5' }}>
              <Typography variant="body2" color="text.secondary">
                Media ore/giorno
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                {workingDays > 0 ? (totalHours / workingDays).toFixed(2) : '0.00'}h
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Giorno</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Entrata 1
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Uscita 1
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Entrata 2
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Uscita 2
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Totale Ore
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Assenza
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Ore attese
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Stato
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingTimesheet ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nessun dato disponibile per questo periodo</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => {
                  const todayISO = getLocalDateKey();
                  const isFutureDay = record.date > todayISO;
                  const totalHoursValue = record.total_hours ?? 0;
                  
                  // Calcola ore permesso per questo giorno
                  const permissionHours = record.leaves?.reduce((sum, leave) => {
                    if (leave.type === 'permesso' && leave.hours) {
                      return sum + leave.hours;
                    }
                    return sum;
                  }, 0) ?? 0;
                  
                  // QUADRATURA: Ore totali giornaliere = ore lavorate + ore permesso
                  const totalDayHours = totalHoursValue + permissionHours;
                  
                  const expectedHoursValue =
                    record.expectedHours != null && !Number.isNaN(record.expectedHours)
                      ? record.expectedHours
                      : null;
                  const tolerance = 0.1;
                  const showFuturePlaceholder =
                    isFutureDay && record.status !== 'holiday' && record.status !== 'leave';
                  
                  // Anomalia: se ore totali (lavorate + permesso) < ore attese
                  let isBelowExpected =
                    expectedHoursValue != null &&
                    record.status !== 'holiday' &&
                    record.status !== 'leave' &&
                    totalDayHours + tolerance < expectedHoursValue;
                  if (showFuturePlaceholder) {
                    isBelowExpected = false;
                  }

                  const isEditingRow = editingRecord?.date === record.date;
                  const lunchBreakMinutes = selectedEmployee?.pausaPranzo ?? 60;
                  const calculatedHoursRaw = isEditingRow
                    ? calculateDurationHours(record.date, editedCheckIn || null, editedCheckOut || null, lunchBreakMinutes)
                    : null;
                  const calculatedHoursRounded =
                    calculatedHoursRaw != null && calculatedHoursRaw >= 0
                      ? Math.round(calculatedHoursRaw * 100) / 100
                      : null;

                  const adminTooltip =
                    record.wasModified && record.lastAdminFullName && record.lastAdminUpdateAt
                      ? `${record.lastAdminFullName} • ${new Date(record.lastAdminUpdateAt).toLocaleString('it-IT')}${
                          record.lastAdminNote ? `\nNote: ${record.lastAdminNote}` : ''
                        }`
                      : record.wasModified && record.lastAdminUpdateAt
                      ? new Date(record.lastAdminUpdateAt).toLocaleString('it-IT')
                      : undefined;

                  return (
                    <TableRow
                      key={record.date}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor:
                          record.status === 'holiday'
                            ? '#fafafa'
                            : isBelowExpected
                            ? 'rgba(255, 152, 0, 0.12)'
                            : record.wasModified
                            ? 'rgba(13, 71, 161, 0.08)'
                            : 'white',
                        borderLeft: isBelowExpected
                          ? '4px solid #ff9800'
                          : record.wasModified
                          ? '4px solid #0d47a1'
                          : undefined,
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {record.date.split('-')[2]} {record.dayOfWeek}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isEditingRow ? (
                          <TextField
                            size="small"
                            type="time"
                            value={editedCheckIn}
                            onChange={(event) => setEditedCheckIn(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                          />
                        ) : (
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              cursor: isRecordDateHoliday(record) ? 'default' : 'pointer',
                              pointerEvents: isRecordDateHoliday(record) ? 'none' : 'auto',
                              '& svg': { fontSize: 16 },
                            }}
                            onClick={() => handleStartEditPresence(record)}
                          >
                            <Typography variant="body2">{record.check_in1 || '-'}</Typography>
                            {record.presenceId && !isRecordDateHoliday(record) && (
                              <Tooltip title="Modifica orari">
                                <EditIcon color="action" sx={{ opacity: 0.6 }} />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isEditingRow ? (
                          <TextField
                            size="small"
                            type="time"
                            value={editedCheckOut}
                            onChange={(event) => setEditedCheckOut(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 60 }}
                          />
                        ) : (
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              cursor: isRecordDateHoliday(record) ? 'default' : 'pointer',
                              pointerEvents: isRecordDateHoliday(record) ? 'none' : 'auto',
                              '& svg': { fontSize: 16 },
                            }}
                            onClick={() => handleStartEditPresence(record)}
                          >
                            <Typography variant="body2">{record.check_out1 || '-'}</Typography>
                            {record.presenceId && !isRecordDateHoliday(record) && (
                              <Tooltip title="Modifica orari">
                                <EditIcon color="action" sx={{ opacity: 0.6 }} />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{record.check_in2 || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{record.check_out2 || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                          {record.total_hours != null && record.total_hours > 0 ? (() => {
                            // Calcola ore permesso per questo giorno
                            const permissionHoursForDay = record.leaves?.reduce((sum, leave) => {
                              if (leave.type === 'permesso' && leave.hours) {
                                return sum + leave.hours;
                              }
                              return sum;
                            }, 0) ?? 0;
                            
                            // Ore lavorate = ore totali - ore permesso
                            const workHours = record.total_hours - permissionHoursForDay;
                            
                            const lunchBreakMinutes = selectedEmployee?.pausaPranzo ?? 60;
                            const lunchBreakHours = lunchBreakMinutes / 60;
                            
                            // Il lordo è calcolato solo dalle ore di lavoro effettive (senza permesso) + pausa
                            // Poi il netto è lordo - pausa = ore lavoro
                            // E il totale giornaliero è ore lavoro + permesso
                            const grossWorkHours = workHours + lunchBreakHours;
                            
                            return (
                              <>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {record.total_hours.toFixed(2)}h
                              </Typography>
                                {permissionHoursForDay > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    ({workHours.toFixed(2)}h lavoro + {permissionHoursForDay.toFixed(2)}h permesso)
                                  </Typography>
                                )}
                                {lunchBreakMinutes > 0 && workHours > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    (Lordo: {grossWorkHours.toFixed(2)}h - {lunchBreakHours.toFixed(2)}h pausa = {workHours.toFixed(2)}h netto)
                                  </Typography>
                                )}
                              </>
                            );
                          })() : (
                            <Typography variant="body2">-</Typography>
                          )}
                              {isBelowExpected && expectedHoursValue != null && (
                                <Typography variant="caption" color="error">
                                  −{(expectedHoursValue - totalDayHours).toFixed(2)}h
                                  {permissionHours > 0 && (
                                    <span style={{ fontSize: '0.65rem', display: 'block' }}>
                                      ({totalHoursValue.toFixed(2)}h lavoro + {permissionHours.toFixed(2)}h permesso)
                                    </span>
                                  )}
                                </Typography>
                              )}
                            </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} flexWrap="wrap">
                          {record.leaves && record.leaves.length > 0 ? (
                            record.leaves.map((leave, index) => {
                              const { letter, color } = getLeaveDisplay(leave.type);
                              const hoursLabel =
                                leave.hours && leave.hours > 0 ? ` ${leave.hours.toFixed(1)}h` : '';
                              // Tooltip con causale per permessi
                              const tooltipTitle = leave.type === 'permesso' && leave.leave_reason
                                ? `Permesso: ${leave.leave_reason}${hoursLabel ? ` (${hoursLabel.trim()})` : ''}`
                                : leave.type === 'permesso'
                                ? `Permesso${hoursLabel ? `: ${hoursLabel.trim()}` : ''}`
                                : `${leave.type}${hoursLabel ? `: ${hoursLabel.trim()}` : ''}`;
                              // Trova il leave_request_id dal leave (se disponibile)
                              const leaveRequestId = (leave as any).leave_request_id;
                              return (
                                <Tooltip key={`${record.date}-leave-${index}`} title={tooltipTitle} arrow>
                                  <Chip
                                    label={`${letter}${hoursLabel}`}
                                    color={color}
                                    size="small"
                                    sx={{
                                      cursor: leave.type === 'permesso' ? 'help' : 'default',
                                    }}
                                    onDelete={leaveRequestId ? () => handleDeleteLeave(leaveRequestId) : undefined}
                                    deleteIcon={
                                      leaveRequestId ? (
                                        <DeleteIcon
                                          fontSize="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteLeave(leaveRequestId);
                                          }}
                                        />
                                      ) : undefined
                                    }
                                  />
                                </Tooltip>
                              );
                            })
                          ) : (
                            <Typography variant="body2">-</Typography>
                          )}
                          {/* Pulsante per aggiungere permesso */}
                          {selectedEmployee && !showFuturePlaceholder && !isRecordDateHoliday(record) && (
                            <Tooltip title="Aggiungi permesso/ferie">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenLeaveDialog(record.date, record)}
                                sx={{
                                  color: '#0d47a1',
                                  '&:hover': {
                                    backgroundColor: '#0d47a1',
                                    color: 'white',
                                  },
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {editingExpectedDate === record.date ? (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TextField
                              size="small"
                              value={editedExpectedHours}
                              type="number"
                              onChange={(event) => setEditedExpectedHours(event.target.value)}
                              inputProps={{ step: 0.25, style: { width: 80 } }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSaveExpectedHours}
                              disabled={savingExpected}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={handleCancelEditExpected} disabled={savingExpected}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {record.expectedHours != null ? `${record.expectedHours.toFixed(2)}h` : '-'}
                            </Typography>
                            <Tooltip title={isRecordDateHoliday(record) ? 'Non modificabile nei giorni festivi' : 'Modifica ore attese per il dipendente'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleStartEditExpected(record)}
                                  disabled={savingExpected || !selectedEmployee || isRecordDateHoliday(record)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            adminTooltip ||
                            (showFuturePlaceholder
                              ? 'Giornata futura'
                              : isBelowExpected && expectedHoursValue != null
                              ? `Previsto: ${expectedHoursValue.toFixed(2)}h, totale: ${totalDayHours.toFixed(2)}h (${totalHoursValue.toFixed(2)}h lavoro${permissionHours > 0 ? ` + ${permissionHours.toFixed(2)}h permesso` : ''})`
                              : record.status === 'leave'
                              ? 'Assenza approvata'
                              : record.status === 'complete'
                              ? 'Giornata completa'
                              : '')
                          }
                        >
                          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                            {record.modifications && record.modifications.length > 0 && (
                              <Tooltip title="Storico modifiche">
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleOpenModDialog(record);
                                  }}
                                  sx={{
                                    color: '#0d47a1',
                                    animation: 'pulseGlow 1.6s ease-in-out infinite',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '@keyframes pulseGlow': {
                                      '0%': { transform: 'scale(1)', boxShadow: '0 0 0 rgba(13,71,161,0)' },
                                      '50%': {
                                        transform: 'scale(1.12)',
                                        boxShadow: '0 0 10px rgba(13,71,161,0.35)',
                                      },
                                      '100%': { transform: 'scale(1)', boxShadow: '0 0 0 rgba(13,71,161,0)' },
                                    },
                                    '&:hover': {
                                      animation: 'none',
                                      transform: 'scale(1.25)',
                                      boxShadow: '0 4px 12px rgba(13, 71, 161, 0.45)',
                                    },
                                  }}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {getStatusIcon(record.status)}
                            <Chip
                              label={
                                showFuturePlaceholder
                                  ? '-'
                                  : isBelowExpected
                                  ? 'Irregolare'
                                  : record.status === 'complete'
                                  ? 'Completo'
                                  : record.status === 'incomplete'
                                  ? 'Incompleto'
                                  : record.status === 'holiday'
                                  ? 'Festivo'
                                  : record.status === 'leave'
                                  ? 'Assenza'
                                  : 'Assente'
                              }
                              color={
                                showFuturePlaceholder
                                  ? ('default' as any)
                                  : isBelowExpected
                                  ? ('warning' as any)
                                  : (getStatusColor(record.status) as any)
                              }
                              sx={
                                showFuturePlaceholder
                                  ? {
                                      backgroundColor: '#e0e0e0',
                                      color: '#424242',
                                    }
                                  : undefined
                              }
                              size="small"
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {isEditingRow ? (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <TextField
                              size="small"
                              value={editedAdminNote}
                              onChange={(event) => setEditedAdminNote(event.target.value)}
                              placeholder="Nota admin"
                              sx={{ width: 160 }}
                            />
                            {calculatedHoursRounded != null && (() => {
                              const lunchBreakMinutes = selectedEmployee?.pausaPranzo ?? 60;
                              const lunchBreakHours = lunchBreakMinutes / 60;
                              const grossHours = calculatedHoursRounded + lunchBreakHours;
                              return (
                                <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                  <Typography variant="caption" color="text.secondary">
                                    Netto: {calculatedHoursRounded.toFixed(2)}h
                                  </Typography>
                                  {lunchBreakMinutes > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      (Lordo: {grossHours.toFixed(2)}h - {lunchBreakHours.toFixed(2)}h pausa)
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()}
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleSavePresenceEdit}
                              disabled={savingPresence}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEditPresence}
                              disabled={savingPresence}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Tooltip title={isRecordDateHoliday(record) ? 'Non modificabile nei giorni festivi' : 'Modifica cartellino'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleStartEditPresence(record)}
                                disabled={savingPresence || isRecordDateHoliday(record)}
                                sx={{
                                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                  '&:hover': {
                                    transform: isRecordDateHoliday(record) ? 'none' : 'scale(1.2)',
                                    boxShadow: isRecordDateHoliday(record) ? 'none' : '0 2px 6px rgba(13, 71, 161, 0.3)',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modDialogOpen} onClose={handleCloseModDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Storico modifiche •{' '}
          {modDialogData ? new Date(modDialogData.date).toLocaleDateString('it-IT') : ''}
        </DialogTitle>
        <DialogContent dividers>
          {modDialogData && modDialogData.modifications.length > 0 ? (
            <List disablePadding>
              {modDialogData.modifications.map((mod, index) => {
                const lines: string[] = [];
                if (mod.oldCheckIn || mod.newCheckIn) {
                  lines.push(`Check-in: ${formatTime(mod.oldCheckIn)} → ${formatTime(mod.newCheckIn)}`);
                }
                if (mod.oldCheckOut || mod.newCheckOut) {
                  lines.push(`Check-out: ${formatTime(mod.oldCheckOut)} → ${formatTime(mod.newCheckOut)}`);
                }
                if (mod.oldWorkHours != null || mod.newWorkHours != null) {
                  lines.push(`Ore lavorate: ${formatHours(mod.oldWorkHours)} → ${formatHours(mod.newWorkHours)}`);
                }
                return (
                  <React.Fragment key={mod.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemText
                        primary={`${mod.adminName || 'Admin'} • ${formatTime(mod.createdAt)}`}
                        secondary={
                          <>
                            {lines.map((line) => (
                              <Typography variant="body2" component="div" key={line}>
                                {line}
                              </Typography>
                            ))}
                            {mod.adminNote && (
                              <Typography variant="body2" component="div">
                                Nota: {mod.adminNote}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    {index < modDialogData.modifications.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <Typography color="text.secondary">Nessuna modifica registrata.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Validazione */}
      <Dialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Conferma Validazione Cartellino
        </DialogTitle>
        <DialogContent>
          {!validationPreview ? (
            <Alert severity="error">
              Errore: Preview non disponibile. Carica prima la preview del cartellino.
            </Alert>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Stai per validare il cartellino di <strong>{selectedEmployee?.fullName}</strong> per il periodo{' '}
                <strong>{monthNames[month]} {year}</strong>.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Una volta validato, per modificarlo sarà necessario revocare la validazione.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Note (opzionale)"
                value={validationNotes}
                onChange={(e) => setValidationNotes(e.target.value)}
                placeholder="Inserisci eventuali note sulla validazione..."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)}>
            Annulla
          </Button>
          <Button
            onClick={handleValidatePresence}
            variant="contained"
            color="primary"
            disabled={loadingValidation || !validationPreview}
          >
            {loadingValidation ? <CircularProgress size={24} /> : 'Conferma Validazione'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Inserimento Permesso/Ferie */}
      <Dialog open={leaveDialogOpen} onClose={handleCloseLeaveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Inserisci {leaveDialogType === 'permesso' ? 'Permesso' : leaveDialogType === 'ferie' ? 'Ferie' : leaveDialogType === 'malattia' ? 'Malattia' : 'Assenza'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              select
              label="Tipo"
              value={leaveDialogType}
              onChange={(e) => setLeaveDialogType(e.target.value as any)}
              fullWidth
            >
              <MenuItem value="permesso">Permesso</MenuItem>
              <MenuItem value="ferie">Ferie</MenuItem>
              <MenuItem value="malattia">Malattia</MenuItem>
              <MenuItem value="altri">Altri</MenuItem>
            </TextField>

            <TextField
              type="date"
              label="Data"
              value={leaveDialogDate || ''}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled
            />

            {leaveDialogType === 'permesso' && (
              <>
                <TextField
                  type="number"
                  label={`Ore (max ${leaveDialogExpectedHours.toFixed(1)}h giornaliere)`}
                  value={leaveDialogHours}
                  onChange={(e) => setLeaveDialogHours(parseFloat(e.target.value) || 0)}
                  fullWidth
                  inputProps={{ min: 0, step: 0.5, max: leaveDialogExpectedHours }}
                  required
                />
                <FormControl fullWidth required>
                  <InputLabel>Causale</InputLabel>
                  <Select
                    value={leaveDialogReason}
                    label="Causale"
                    onChange={(e) => setLeaveDialogReason(e.target.value)}
                  >
                    <MenuItem value="Studio">Studio</MenuItem>
                    <MenuItem value="ROL (Riduzione Orario Lavorativo)">ROL (Riduzione Orario Lavorativo)</MenuItem>
                    <MenuItem value="Lutto">Lutto</MenuItem>
                    <MenuItem value="Matrimonio">Matrimonio</MenuItem>
                    <MenuItem value="Malattia del figlio">Malattia del figlio</MenuItem>
                    <MenuItem value="Esami">Esami</MenuItem>
                    <MenuItem value="Visite mediche">Visite mediche</MenuItem>
                    <MenuItem value="Motivi personali">Motivi personali</MenuItem>
                    <MenuItem value="Donazione sangue">Donazione sangue</MenuItem>
                    <MenuItem value="Accompagnamento familiare">Accompagnamento familiare</MenuItem>
                    <MenuItem value="Adozione/Affidamento">Adozione/Affidamento</MenuItem>
                    <MenuItem value="Congedo parentale">Congedo parentale</MenuItem>
                    <MenuItem value="Altro">Altro</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <TextField
              multiline
              rows={3}
              label="Note (opzionale)"
              value={leaveDialogNotes}
              onChange={(e) => setLeaveDialogNotes(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeaveDialog}>Annulla</Button>
          <Button
            onClick={handleSaveLeave}
            variant="contained"
            disabled={savingLeave || (leaveDialogType === 'permesso' && (!leaveDialogReason || leaveDialogHours <= 0))}
          >
            {savingLeave ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminTimesheet;
